<template>
	<div class="client-filter-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="client-filter-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="client-filter-modal__search-input"
				placeholder="Поиск"
				show-search-button
			/>
		</div>
		<div class="client-filter-modal__header">
			<div class="client-filter-modal__header-title">
				Клиенты
			</div>
		</div>
		<div
			class="client-filter-modal__body"
			ref="$body"
		>
			<div class="client-filter-modal__body-item">
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="store.byBase"
					text=""
					@input="onToggleByBase"
				/>
				<div
					class="client-filter-modal__body-item-text"
					@click="onToggleByBase(!store.byBase)"
				>
					По базе
				</div>
			</div>

			<template
				v-for="opt in filteredOptions"
				:key="opt.Id"
			>
				<div class="client-filter-modal__body-item">
					<app-checkbox
						:style="{ height: '16px', marginRight: '8px' }"
						:value="isChecked(opt.Id, true)"
						text=""
						@input="(on) => onToggleClient(opt, on)"
					/>
					<div
						class="client-filter-modal__body-item-text"
						@click="onToggleClient(opt, !isChecked(opt.Id, true))"
					>
						{{ opt.Name }}<span
							v-if="opt.Union.length"
							class="client-filter-modal__body-item-holding"
						> (с холдингом)</span>
					</div>
				</div>
				<div
					v-for="member in opt.Union"
					:key="member.Id"
					class="client-filter-modal__body-item client-filter-modal__body-item--union"
				>
					<app-checkbox
						:style="{ height: '16px', marginRight: '8px' }"
						:value="isChecked(member.Id, false)"
						text=""
						@input="(on) => onToggleUnion(member, on)"
					/>
					<div
						class="client-filter-modal__body-item-text"
						@click="onToggleUnion(member, !isChecked(member.Id, false))"
					>
						{{ member.Name }}
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import {
	searchStore,
	ClientFilterOption,
	ClientFilterUnion
} from "@/store/search";

const emit = defineEmits(['change'])

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = searchStore()

const filter = ref('')

const isLoading = computed(() => store.isClientFilterLoading)

// Подсветка чекбокса по паре (Id, UseUnion): верхнеуровневый «с холдингом» (true) и
// вложенный (false) могут иметь одинаковый Id, но выбран может быть только один из них.
const isChecked = (id: number, useUnion: boolean) =>
	store.selectedClients.some(c => c.Id === id && c.UseUnion === useUnion)

const filteredOptions = computed<ClientFilterOption[]>(() => {
	const query = filter.value.trim().toLowerCase()
	if (!query) {
		return store.clientFilterList
	}

	return store.clientFilterList.filter(opt =>
		opt.Name?.toLowerCase().includes(query)
		|| opt.Union.some(m => m.Name?.toLowerCase().includes(query))
	)
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const onToggleByBase = (on: boolean) => {
	store.setByBase(on)
	emit('change')
}

const onToggleClient = (opt: ClientFilterOption, on: boolean) => {
	store.toggleSelectedClient({ Id: opt.Id, Name: opt.Name, UseUnion: true }, on)
	emit('change')
}

const onToggleUnion = (member: ClientFilterUnion, on: boolean) => {
	store.toggleSelectedClient({ Id: member.Id, Name: member.Name, UseUnion: false }, on)
	emit('change')
}

onMounted(async () => {
	await nextTick(() => $input.value?.focusInput())
})
</script>

<style scoped lang="scss">
.client-filter-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	flex: 1;
	min-height: 0;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		min-height: 0;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-item {
			display: flex;
			align-items: center;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&--union {
				margin-left: 8px;
			}

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&-holding {
				color: #979797;
			}

			&:hover {
				text-decoration: underline;
				color: #396742;
			}
		}
	}
}
</style>
